// src/services/subCategoryService.js
import { repo } from "../dbservice/repo";
import { SubCategoryModel } from "../models/SubCategoryModel";
import { categoryService } from "./categoryService";
import { singleValueListService } from "./singleValueListService";

class SubCategoryService {
  constructor() {
    this.collectionName = "subcategories"; 
  }

  get currentUser() {
    if (typeof window === "undefined") return null;
    try {
      const data = localStorage.getItem("userData");
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.error("[SubCategoryService] Failed to parse userData", e);
      return null;
    }
  }

  /**
   * Backwards-compatible normalization mapping legacy values to current catalog keys.
   */
  _normalizeSubCategoryOptions(data = {}) {
    const normalized = { ...data };

    // 1. Normalize tracking method (serializationType)
    if (normalized.serializationType) {
      normalized.serializationType = String(normalized.serializationType).trim().toLowerCase();
    }

    // 2. Normalize depreciation methods (straight-line -> SLM, written-down-value -> WDV)
    if (normalized.depreciation) {
      const dep = String(normalized.depreciation).trim();
      if (dep === "straight-line") {
        normalized.depreciation = "SLM";
      } else if (dep === "written-down-value") {
        normalized.depreciation = "WDV";
      } else if (dep.toLowerCase() === "none") {
        normalized.depreciation = "None";
      }
    }

    // 3. Clear dependent calculations based on depreciation type selection
    if (normalized.depreciation === "SLM") {
      normalized.depreciationRate = null;
    } else if (normalized.depreciation === "WDV") {
      normalized.usefulLife = null;
    } else if (!normalized.depreciation || normalized.depreciation === "None") {
      normalized.usefulLife = null;
      normalized.depreciationRate = null;
    }

    return normalized;
  }

  /**
   * Referential Integrity Check: Verifies that the parent category exists
   * and belongs to the same company tenant.
   */
  async _validateParentCategory(categoryId, companyId) {
    if (!categoryId || String(categoryId).trim() === "") {
      throw new Error(
        "Parent Category ID must be selected to configure a sub-category."
      );
    }

    const category = await categoryService.fetchById(categoryId);
    if (!category) {
      throw new Error(
        `Referential Integrity Constraint: Parent Category matching ID "${categoryId}" does not exist.`
      );
    }

    if (
      companyId &&
      category.companyId &&
      String(category.companyId) !== String(companyId)
    ) {
      throw new Error(
        "Data Boundary Error: Parent Category is owned by another corporate tenant workspace."
      );
    }

    return category;
  }

  /**
   * Catalog Integration: Validates inputs directly against your dynamic SVL entries
   */
  async _validateSVLOptions(data, companyId) {
    // Validate serializationType
    if (data.serializationType) {
      const serialOptions = await singleValueListService.getItems("serialization_types", { companyId });
      const isValidSerial = serialOptions.some(opt => String(opt.value).toLowerCase() === String(data.serializationType).toLowerCase());
      if (!isValidSerial && serialOptions.length > 0) {
        throw new Error(`Referential Validation Error: Tracking Method "${data.serializationType}" does not exist in SVL catalog.`);
      }
    }

    // Validate depreciation
    if (data.depreciation && data.depreciation !== "None" && data.depreciation !== "") {
      const depOptions = await singleValueListService.getItems("depreciation_methods", { companyId });
      const isValidDep = depOptions.some(opt => String(opt.value).toUpperCase() === String(data.depreciation).toUpperCase());
      if (!isValidDep && depOptions.length > 0) {
        throw new Error(`Referential Validation Error: Depreciation Method "${data.depreciation}" does not exist in SVL catalog.`);
      }
    }
  }

  /**
   * Create a new sub-category (validates parent category first).
   */
  async create(data, currentUser = {}) {
    const user = currentUser?.email ? currentUser : this.currentUser;
    const targetId = data.id || data.subCategoryId || crypto.randomUUID();
    const resolvedCompanyId = data.companyId || user?.companyId || null;

    // Enforce referential integrity before proceeding
    await this._validateParentCategory(data.categoryId, resolvedCompanyId);

    // Apply normalization & validate against dynamic SVL options
    const normalizedData = this._normalizeSubCategoryOptions(data);
    await this._validateSVLOptions(normalizedData, resolvedCompanyId);

    const payload = {
      ...normalizedData,
      id: targetId,
      subCategoryId: normalizedData.subCategoryId || targetId,
      companyId: resolvedCompanyId,
      metadata: {
        ...(normalizedData.metadata || {}),
        createdBy: user?.email || "system",
        updatedBy: user?.email || "system",
      },
    };

    return await this.upsertData(targetId, payload, { isCreation: true });
  }

  /**
   * Fetch sub-categories, optionally filtered by parent category.
   */
  async fetchSubCategories({
    companyId = "",
    categoryId = "",
    status = "",
  } = {}) {
    const user = this.currentUser;
    if (!companyId || companyId.toString().trim() === "") {
      companyId = user?.companyId;
    }

    const filters = [];
    if (companyId) {
      filters.push({ field: "companyId", value: String(companyId) });
    }
    if (categoryId) {
      filters.push({ field: "categoryId", value: String(categoryId) });
    }
    if (status) {
      filters.push({ field: "status", value: status });
    }

    try {
      return (await repo.fetchData(this.collectionName, filters)) || [];
    } catch (error) {
      console.error(
        "[SubCategoryService] Failed to fetch subcategories:",
        error
      );
      return [];
    }
  }

  /**
   * Fetch a single sub-category by ID.
   */
  async fetchById(id) {
    if (!id) {
      console.error("[SubCategoryService] fetchById: ID missing");
      return null;
    }
    try {
      return (await repo.fetchById(this.collectionName, id)) || null;
    } catch (error) {
      console.error(
        `[SubCategoryService] Failed to fetch subcategory ${id}:`,
        error
      );
      return null;
    }
  }

  /**
   * Update an existing sub-category (re-validates parent if categoryId changes).
   */
  async update(id, data, currentUser = {}) {
    if (!id) throw new Error("Sub-Category ID is required for update.");
    const user = currentUser?.email ? currentUser : this.currentUser;
    const resolvedCompanyId = data.companyId || user?.companyId || null;

    // Re-validate parent category if it's being changed
    if (data.categoryId) {
      await this._validateParentCategory(data.categoryId, resolvedCompanyId);
    }

    // Apply normalization & validate against dynamic SVL options
    const normalizedData = this._normalizeSubCategoryOptions(data);
    await this._validateSVLOptions(normalizedData, resolvedCompanyId);

    const payload = {
      ...normalizedData,
      id,
      metadata: {
        ...(normalizedData.metadata || {}),
        updatedBy: user?.email || "system",
      },
    };

    return await this.upsertData(id, payload, { isCreation: false });
  }

  /**
   * Core persistence orchestrator with read-before-write metadata preservation.
   */
  async upsertData(targetId, data, { isCreation = false } = {}) {
    if (!targetId) throw new Error("Target Sub-Category ID is required");

    let finalData = { ...data };

    if (!isCreation) {
      const existing = await this.fetchById(targetId);
      if (existing) {
        finalData = {
          ...existing,
          ...data,
          metadata: {
            ...(existing.metadata || {}),
            ...(data.metadata || {}),
            createdAt:
              existing.metadata?.createdAt ||
              existing.createdAt ||
              new Date().toISOString(),
            createdBy:
              existing.metadata?.createdBy || existing.createdBy || "system",
          },
        };
      }
    }

    const instance = new SubCategoryModel(
      { ...finalData, id: targetId },
      { isCreation }
    );

    if (!instance.isValid()) {
      throw new Error(
        "Invalid subcategory payload: subCategoryId, subCategoryName, and categoryId are required."
      );
    }

    const payload = instance.toPayload();
    const nowIso = new Date().toISOString();

    payload.metadata = {
      ...(payload.metadata || {}),
      updatedAt: nowIso,
      updatedBy:
        data.metadata?.updatedBy || payload.metadata?.updatedBy || "system",
    };

    return await repo.upsertData(this.collectionName, targetId, payload);
  }

  /**
   * Delete a sub-category record.
   */
  async delete(id) {
    if (!id) throw new Error("Sub-Category ID is required for deletion.");
    try {
      await repo.deleteData(this.collectionName, id);
      return { success: true };
    } catch (error) {
      console.error(
        `[SubCategoryService] Failed to delete subcategory ${id}:`,
        error
      );
      throw error;
    }
  }
}

export const subCategoryService = new SubCategoryService();